// Code generated by apic; DO NOT EDIT.
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import type { ServiceMetadata } from "./types";
import type { ApicClient } from "../react/client";
import { SERVICE_METADATA } from "./metadata";
import { EndpointList } from "./EndpointList";
import { TokenBar } from "./TokenBar";
import { useExplorerClient } from "./useExplorerClient";
import { GraphQLPanel } from "./GraphQLPanel";
import { WebSocketPanel } from "./WebSocketPanel";
import { McpPanel } from "./McpPanel";

/**
 * ServiceExplorer is the top-level service explorer component. It renders a
 * tab/section per non-empty surface, defaulting the metadata to the generated
 * SERVICE_METADATA when no explicit metadata prop is provided.
 *
 * Props:
 *   metadata  - the ServiceMetadata to explore; defaults to SERVICE_METADATA.
 *   baseUrl   - optional base URL prefix forwarded to EndpointList and panels.
 *   className - extra CSS class appended to the root element.
 *
 * Surfaces rendered:
 *   REST (always)  - via EndpointList over metadata.rest, passing metadata.schemas.
 *   GraphQL        - via GraphQLPanel over metadata.graphql when non-empty.
 *   WebSocket      - via WebSocketPanel over metadata.websockets when non-empty.
 *   MCP            - via McpPanel over metadata.mcp when non-empty.
 *
 * The tab selector uses useState; no web-global access occurs at module load
 * time (RN-safe).
 */

type TabId = "rest" | "graphql" | "websocket" | "mcp";

export function ServiceExplorer(props: {
  metadata?: ServiceMetadata;
  baseUrl?: string;
  className?: string;
}): JSX.Element {
  const { metadata = SERVICE_METADATA, baseUrl, className } = props;

  // Determine which tabs are available based on non-empty surfaces.
  const hasGraphQL = metadata.graphql !== null && metadata.graphql.length > 0;
  const hasWS = metadata.websockets.length > 0;
  const hasMCP = metadata.mcp.length > 0;

  // useState: no web globals accessed here; initial state is computed from props.
  // Safe to call at render time (RN-safe).
  const [activeTab, setActiveTab] = useState<TabId>("rest");

  // Build a shared client for all EndpointCards so the TryPanel instances reuse
  // the same client (and its internal state) rather than each creating one.
  const client: ApicClient = useExplorerClient(baseUrl);

  const root = ["apic-ui-service-explorer", className].filter(Boolean).join(" ");

  return (
    <div className={root}>
      {/* TokenBar — bearer-token input that feeds through to useExplorerClient */}
      <TokenBar className="apic-ui-service-explorer-token-bar" />

      {/* N1: OIDC/JWT issuer note — rendered when a service-wide issuer is configured.
          The issuer URL is a public, non-secret value (same as the OIDC discovery
          document issuer). Displayed so developers know which IdP issues tokens. */}
      {metadata.oidcIssuer !== "" && (
        <div className="apic-ui-service-explorer-oidc-note">
          <span className="apic-ui-chip apic-ui-chip--oidc">
            {"OIDC/JWT issuer: " + metadata.oidcIssuer}
          </span>
        </div>
      )}

      {/* Tab bar — REST always present; optional surfaces when non-empty */}
      <div className="apic-ui-tab-bar" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === "rest"}
          className={"apic-ui-tab" + (activeTab === "rest" ? " apic-ui-tab--active" : "")}
          onClick={() => setActiveTab("rest")}
        >
          {"REST (" + metadata.rest.length + ")"}
        </button>

        {hasGraphQL && (
          <button
            role="tab"
            aria-selected={activeTab === "graphql"}
            className={"apic-ui-tab" + (activeTab === "graphql" ? " apic-ui-tab--active" : "")}
            onClick={() => setActiveTab("graphql")}
          >
            {"GraphQL (" + (metadata.graphql !== null ? metadata.graphql.length : 0) + ")"}
          </button>
        )}

        {hasWS && (
          <button
            role="tab"
            aria-selected={activeTab === "websocket"}
            className={"apic-ui-tab" + (activeTab === "websocket" ? " apic-ui-tab--active" : "")}
            onClick={() => setActiveTab("websocket")}
          >
            {"WebSocket (" + metadata.websockets.length + ")"}
          </button>
        )}

        {hasMCP && (
          <button
            role="tab"
            aria-selected={activeTab === "mcp"}
            className={"apic-ui-tab" + (activeTab === "mcp" ? " apic-ui-tab--active" : "")}
            onClick={() => setActiveTab("mcp")}
          >
            {"MCP (" + metadata.mcp.length + ")"}
          </button>
        )}
      </div>

      {/* REST panel — always rendered when activeTab === "rest" */}
      {activeTab === "rest" && (
        <section className="apic-ui-panel apic-ui-panel--rest">
          <EndpointList
            endpoints={metadata.rest}
            schemas={metadata.schemas}
            client={client}
            {...(baseUrl !== undefined ? { baseUrl } : {})}
          />
        </section>
      )}

      {/* GraphQL panel */}
      {hasGraphQL && activeTab === "graphql" && (
        <section className="apic-ui-panel apic-ui-panel--graphql">
          <GraphQLPanel
            ops={metadata.graphql ?? []}
            client={client}
            className="apic-ui-graphql-panel"
          />
        </section>
      )}

      {/* WebSocket panel */}
      {hasWS && activeTab === "websocket" && (
        <section className="apic-ui-panel apic-ui-panel--websocket">
          <WebSocketPanel
            endpoints={metadata.websockets}
            client={client}
            className="apic-ui-websocket-panel"
            {...(baseUrl !== undefined ? { baseUrl } : {})}
          />
        </section>
      )}

      {/* MCP panel */}
      {hasMCP && activeTab === "mcp" && (
        <section className="apic-ui-panel apic-ui-panel--mcp">
          <McpPanel
            tools={metadata.mcp}
            client={client}
            schemas={metadata.schemas}
            mcpAuthMode={metadata.mcpAuthMode}
            className="apic-ui-mcp-panel"
            {...(baseUrl !== undefined ? { baseUrl } : {})}
          />
        </section>
      )}
    </div>
  );
}
